import type { AnalysisState, VisualizationType } from '@aibi/contracts';
import { AppError, err, type Result } from '@aibi/shared';

/**
 * Contexto que se entrega al asistente: solo intencion analitica e identificadores.
 * Nunca incluye filas, resultados, credenciales ni metadata de conexion.
 */
export interface AssistantContext {
  readonly semanticModelId: string;
  readonly analysisState: AnalysisState | null;
}

export interface AssistantRequest {
  readonly prompt: string;
  readonly context: AssistantContext;
}

export interface AssistantCandidate {
  readonly id: string;
  readonly label: string;
  readonly kind: 'dimension' | 'measure' | 'member' | 'visualization' | 'filter';
  readonly dimensionId?: string;
  /** Instruccion reformulada si el usuario elige este candidato; `null` si no aplica. */
  readonly followUp: string | null;
}

export interface AssistantModelChange {
  readonly actionId: string;
  readonly summary: string;
}

export interface AssistantFact {
  readonly label: string;
  readonly value: string;
  readonly source: 'analysis-state' | 'semantic-model' | 'query-plan' | 'query-result';
}

interface ReplyTrace {
  readonly requestId: string;
}

/**
 * Respuesta tipada del asistente. Ninguna variante modifica el workspace: las propuestas
 * se aplican solo cuando el usuario lo confirma (comando `apply-ai-proposal`).
 */
export type AssistantReply =
  | (ReplyTrace & {
      readonly kind: 'proposal';
      readonly summary: readonly string[];
      readonly rationale?: string;
      /** Tarjeta sobre la que se calculo la propuesta; `null` = crear una tarjeta nueva. */
      readonly baseState: AnalysisState | null;
      /** Estado candidato validado; `null` si la propuesta solo contiene cambios de modelo. */
      readonly state: AnalysisState | null;
      /** Cambios al Semantic Model: requieren confirmacion y no se aplican desde la UI. */
      readonly modelChanges: readonly AssistantModelChange[];
    })
  | (ReplyTrace & {
      readonly kind: 'clarification';
      readonly question: string;
      readonly reason: string;
      readonly candidates: readonly AssistantCandidate[];
    })
  | (ReplyTrace & {
      readonly kind: 'explanation';
      readonly title: string;
      readonly summary: string;
      readonly facts: readonly AssistantFact[];
    })
  | (ReplyTrace & {
      readonly kind: 'suggestion';
      readonly cardId: string | null;
      readonly visualization: VisualizationType;
      readonly label: string;
      readonly rationale: string;
    })
  | (ReplyTrace & {
      readonly kind: 'error';
      readonly code: string;
      readonly message: string;
      readonly candidates: readonly AssistantCandidate[];
    });

/**
 * Estado de conexion mostrado en la UI:
 * - `local`: interprete local de reglas en el navegador (sin red).
 * - `unverified`: el servidor tiene proveedor configurado pero aun no hubo una llamada real.
 * - `connected` / `failing`: resultado de la ultima llamada real al proveedor del servidor.
 * - `unconfigured`: sin proveedor; el asistente no responde.
 */
export type AssistantConnection = 'local' | 'unverified' | 'connected' | 'failing' | 'unconfigured';

/**
 * Puerto del asistente de IA en la UI. El panel depende solo de esta interfaz; la
 * implementacion por defecto usa el AI Core en proceso con el proveedor local de reglas.
 */
export interface AssistantPort {
  /** `false` si no hay proveedor configurado: la UI lo indica sin intentar llamadas. */
  readonly available: boolean;
  /** Nombre del proveedor para la UI (nunca credenciales ni endpoints). */
  readonly providerName: string | null;
  readonly connection: AssistantConnection;
  /** Notifica cambios de `available`, `providerName` o `connection`; opcional si son fijos. */
  subscribe?(listener: () => void): () => void;
  ask(request: AssistantRequest, signal?: AbortSignal): Promise<Result<AssistantReply, AppError>>;
}

/** Asistente sin proveedor: responde siempre con un error de configuracion. */
export function createUnconfiguredAssistant(): AssistantPort {
  return {
    available: false,
    providerName: null,
    connection: 'unconfigured',
    ask: () =>
      Promise.resolve(
        err(
          new AppError(
            'CONFIGURATION_ERROR',
            'No hay un proveedor de IA configurado. El asistente estara disponible en una proxima version.',
          ),
        ),
      ),
  };
}
